import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import minMax from 'dayjs/plugin/minMax';
import { S3PresignedPost } from 'types/api';

dayjs.extend(utc);
dayjs.extend(minMax);

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export const compact = <T>(arr: T[]): T[] => arr.filter((item) => !!item);

export const isNil = (value: unknown): value is null | undefined =>
    value === null || value === undefined;

export const isUUID = (value: string) => {
    if (!value) return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        value,
    );
};

export const getLastItem = <T>(arr: T[]): T => {
    return arr[arr.length - 1];
};

export const getInitials = (name: string, maxLength = 3) => {
    return name
        .split(' ')
        .map((n) => n[0])
        .slice(0, maxLength)
        .join('');
};

export const formatDate = (
    date: Date,
    granularity: 'date' | 'minute' | 'second' | 'millisecond' = 'date',
) => {
    switch (granularity) {
        case 'date':
            return dayjs.utc(date).local().format('MM/DD/YYYY');
        case 'minute':
            return dayjs.utc(date).local().format('MM/DD/YYYY h:mm A');
        case 'second':
            return dayjs.utc(date).local().format('MM/DD/YYYY h:mm:ss A');
        case 'millisecond':
            return dayjs.utc(date).local().format('MM/DD/YYYY h:mm:ss.SSS A');
    }
};

export const getLocalDate = (date: Date): Date => {
    return dayjs.utc(date).local().toDate();
};

export const maxFormattedDate = (dates: Date[]) => {
    return (
        dayjs
            .max(...dates.map((date) => dayjs.utc(date).local()))
            ?.format('MM/DD/YYYY h:mm A') ?? 'N/A'
    );
};

const formatDecimal = (num: number) => {
    return Number.isInteger(num) ? num.toString() : num.toFixed(2);
};

export const formatCents = (cents: number) => {
    return `¢${formatDecimal(cents)}`;
};

export const formatDollars = (dollars: number) => {
    return `$${formatDecimal(dollars)}`;
};

export const formatCurrency = (currency: number) => {
    if (currency < 1) {
        return formatCents(currency * 100);
    }
    return formatDollars(currency);
};

export const formatSeconds = ({
    seconds = 0,
    milliseconds = 0,
}: {
    seconds?: number;
    milliseconds?: number;
}) => {
    const totalSeconds = seconds + milliseconds / 1000;
    return `${totalSeconds.toFixed(2)}s`;
};

export const formatPercentage = (percentage: number) => {
    // only add 2 decimal places if there are any
    const formattedPercentage = (percentage * 100).toFixed(2);
    if (formattedPercentage.endsWith('.00')) {
        return `${percentage * 100}%`;
    }
    return formattedPercentage;
};

export const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
};

export const shuffleArray = <T>(array: T[]): void => {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
};

export const uploadToS3 = async (
    post: S3PresignedPost,
    file: File,
): Promise<string> => {
    const formData = new FormData();

    // The order of fields is important. The file must be the last field.
    Object.entries(post.fields).forEach(([key, value]) => {
        formData.append(key, value);
    });

    if (!post.fields['Content-Type']) {
        formData.append('Content-Type', file.type);
    }

    formData.append('file', file);

    const response = await fetch(post.url, {
        method: 'POST',
        body: formData,
    });

    if (response.status !== 204) {
        const errorText = await response.text();
        console.error('S3 Upload Error:', errorText);
        throw new Error('Failed to upload file to S3.');
    }

    const url = new URL(post.url);
    return `${url.origin}/${post.fields.key}`;
};
